// Review-photo lightbox (page-11 Customer Reviews hero strip). The strip's
// own slider (drag-to-scroll, arrows) is the shared <skullette-rail>
// component from skullette-rails.js; this element only owns the
// click-to-enlarge overlay + Esc/prev/next navigation.
//
// Click-suppression after a drag: skullette-rail already stops a click from
// ever reaching an image inside the rail once a drag exceeded its own >5px
// threshold (it calls stopPropagation() on the rail during the capturing
// phase, before the event can reach a child <img>'s own listener). That
// makes the images' click listeners below naturally inert after a drag, so
// no duplicate drag-tracking logic lives here.
//
// No-JS: the strip is a plain overflow scroller (works without this file);
// the lightbox never opens without JS, by design.
class SkulletteReviewLightbox extends HTMLElement {
  connectedCallback() {
    // editor move / morph re-runs connectedCallback - don't double-bind
    if (this._bound) return;
    this._bound = true;
    const group = this.getAttribute('data-group');
    this.shots = group
      ? Array.from(document.querySelectorAll(`[data-lightbox-group="${group}"] img[data-full-src]`))
      : [];
    if (!this.shots.length) return;

    this.imgEl = this.querySelector('[data-lbox-img]');
    this.capEl = this.querySelector('[data-lbox-cap]');
    this.current = 0;

    this._onShotClick = (index) => this.open(index);
    this.shots.forEach((shot, index) => {
      shot.addEventListener('click', () => this._onShotClick(index));
      // keyboard users need a way in: the strip thumbs act as buttons
      shot.setAttribute('tabindex', '0');
      shot.setAttribute('role', 'button');
      shot.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._onShotClick(index);
        }
      });
    });

    this.querySelector('[data-lbox-close]')?.addEventListener('click', () => this.close());
    this.querySelector('[data-lbox-prev]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.show(this.current - 1);
    });
    this.querySelector('[data-lbox-next]')?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.show(this.current + 1);
    });
    this.addEventListener('click', (event) => {
      if (event.target === this) this.close();
    });

    this._onKeydown = (event) => {
      if (this.hidden) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowLeft') this.show(this.current - 1);
      if (event.key === 'ArrowRight') this.show(this.current + 1);
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  disconnectedCallback() {
    if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
    // never leave the page scroll-locked if removed while open
    if (!this.hidden) document.body.style.overflow = '';
  }

  show(index) {
    this.current = (index + this.shots.length) % this.shots.length;
    const shot = this.shots[this.current];
    this.imgEl.src = shot.dataset.fullSrc;
    this.imgEl.alt = shot.alt;
    // The width/height ATTRIBUTES carry the upload's intrinsic size; the
    // .width property would return the rendered thumbnail box (~196px) and
    // shrink the "enlarged" view to thumb size.
    const w = shot.getAttribute('width');
    const h = shot.getAttribute('height');
    if (w) this.imgEl.width = w;
    if (h) this.imgEl.height = h;
    this.capEl.textContent = shot.alt;
  }

  open(index) {
    this.show(index);
    this.hidden = false;
    document.body.style.overflow = 'hidden';
    this._lastFocus = document.activeElement;
    this.querySelector('[data-lbox-close]')?.focus();
  }

  close() {
    this.hidden = true;
    document.body.style.overflow = '';
    if (this._lastFocus?.focus) this._lastFocus.focus();
  }
}

if (!customElements.get('skullette-review-lightbox')) {
  customElements.define('skullette-review-lightbox', SkulletteReviewLightbox);
}
